import { CocoEvent } from '@cotera/client/app/etc';
import { z } from 'zod/v3';
import { err, ok } from 'neverthrow';
import { BroadcastEventType } from '@cotera/api';
import { emit } from '@cotera/client/app/etc/broadcast-event-bus';
import {
  ActionResult,
  ActionTrackMetadata,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import { toast } from '@cotera/client/app/components/ui';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import { DEFAULT_DATASET_QUERY, DatasetModel } from './dataset.model';

const saveDatasetParamsSchema = z.object({
  datasetId: z.string().optional(),
  /** Navigation saves pass silent:true; those are not tracked. */
  silent: z.boolean().optional(),
});

type Params = z.infer<typeof saveDatasetParamsSchema>;

type SaveDatasetActionClient = {
  resource: {
    dataset: Pick<TenantedClient['resource']['dataset'], 'updateSettings'>;
  };
};

/**
 * Persists dataset-level settings — currently the preview query. Editing the
 * query only changes what the grid reads through; it is not stored until this
 * runs, which is what the unsaved indicator reflects.
 */
export class SaveDatasetAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'Save Query';
  description = 'Save the dataset preview query';
  inputSchema = saveDatasetParamsSchema;
  shortcut = ['ctrl', 's'];
  group = 'Dataset';
  icon = 'save';

  private _lastSavedDatasetId: string | null = null;
  private _lastSaveWasSilent = false;

  constructor(
    private readonly client: SaveDatasetActionClient,
    private readonly orgId: string
  ) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    return this.getTargetModel(context) !== null;
  }

  getPriority(context: ApplicableContext): number {
    const model = this.getTargetModel(context);
    if (model === null) {
      return 0;
    }
    // Outrank nothing when clean so Ctrl+S is not claimed for a no-op.
    return model.hasUnsavedChangesWatchable.snapshot() ? 30 : 5;
  }

  shouldAsk(): boolean {
    return false;
  }

  async execute(
    payload: Params,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const model = this.getTargetModel(context);
    if (model === null) {
      return err([{ t: 'error', message: 'No dataset in scope' }]);
    }

    // A query is as much a first edit as a rename or a column is, so it brings
    // the dataset into being the same way those do. Without this, saving a
    // query on a dataset that has never been written fails as "not found".
    const persisted = await model.persist();
    if (persisted.isErr()) {
      toast.error(persisted.error);
      return err([{ t: 'error', message: persisted.error }]);
    }

    const query = model.previewQueryWatchable.snapshot().trim();
    const result = await this.client.resource.dataset.updateSettings({
      id: model.datasetId,
      // The default is stored as null so a dataset without a custom query
      // stays indistinguishable from one that was reset.
      previewQuery:
        query === DEFAULT_DATASET_QUERY || query === '' ? null : query,
    });

    if (result.isErr()) {
      const message =
        result.error.t === 'application'
          ? result.error.err.message
          : 'Failed to save dataset';
      toast.error(message);
      return err([{ t: 'error', message }]);
    }

    model.markPreviewQuerySaved(query === '' ? DEFAULT_DATASET_QUERY : query);

    emit(BroadcastEventType.ResourceUpdated, {
      orgId: this.orgId,
      resourceType: 'dataset',
      resourceId: model.datasetId,
    });

    this._lastSavedDatasetId = model.datasetId;
    this._lastSaveWasSilent = payload.silent === true;

    if (payload.silent !== true) {
      toast.success('Dataset saved');
    }
    return ok({});
  }

  track(): ActionTrackMetadata | null {
    if (this._lastSavedDatasetId === null || this._lastSaveWasSilent) {
      return null;
    }
    return {
      event: CocoEvent.DatasetSaved,
      properties: { datasetId: this._lastSavedDatasetId },
    };
  }

  private getTargetModel(context: ApplicableContext): DatasetModel | null {
    return context.targetedModelOfType(DatasetModel);
  }
}
